import { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { getProdutos, getProdutoUltimasCompras, getProdutoUltimasVendas } from '../services/api';
import { PageHeader, SearchBar, Table, Loading, Erro, Badge, Paginacao, Modal } from '../components/UI';
import './Produtos.css';

const LIMITE = 50;
const fmt    = v => v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
const fmtNum = v => v != null ? v.toLocaleString('pt-BR') : '—';
const fmtData = v => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('pt-BR'); } catch { return v; }
};

const emPromocao = row => {
  if (!row.VALOR_DA_PROMOCAO || !row.PROMOCAO_DATA_INICIO || !row.PROMOCAO_DATA_FINAL) return false;
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const ini  = new Date(row.PROMOCAO_DATA_INICIO); ini.setHours(0, 0, 0, 0);
  const fim  = new Date(row.PROMOCAO_DATA_FINAL);  fim.setHours(0, 0, 0, 0);
  return hoje >= ini && hoje <= fim;
};

const colunas = [
  {
    key: 'ID', label: 'ID', width: 60,
    render: v => <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>{v}</span>,
  },
  { key: 'PRODUTO', label: 'Descrição', width: 320 },
  {
    key: 'EAN', label: 'EAN/Cód', width: 130,
    render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v || '—'}</span>,
  },
  {
    key: 'ESTOQUEFISICO', label: 'Estoque', width: 90,
    render: v => <span style={{ fontWeight: 700, color: v > 0 ? 'var(--green)' : 'var(--red)' }}>{fmtNum(v ?? 0)}</span>,
  },
  {
    key: 'VENDA', label: 'Preço Venda', width: 140,
    render: (v, row) => {
      if (emPromocao(row)) {
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ textDecoration: 'line-through', color: 'var(--text-dim)', fontSize: 11 }}>{fmt(v)}</span>
            <span style={{ fontWeight: 700, color: 'var(--green)' }}>{fmt(row.VALOR_DA_PROMOCAO)}</span>
            <Badge valor="Promo" tipo="warning" />
          </span>
        );
      }
      return <span style={{ fontWeight: 700, color: 'var(--laranja)' }}>{fmt(v)}</span>;
    },
  },
];

function BarcodeScanner({ onDetect, onClose }) {
  const videoRef = useRef(null);
  const [status, setStatus] = useState('iniciando'); // iniciando | ok | erro

  useEffect(() => {
    const reader = new BrowserMultiFormatReader();
    const ctrl = { current: null };
    let parado = false;

    reader.decodeFromConstraints(
      { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } } },
      videoRef.current,
      (result) => {
        if (parado || !result) return;
        parado = true;
        ctrl.current?.stop();
        onDetect(result.getText());
      }
    )
      .then(c => {
        ctrl.current = c;
        if (parado) c.stop();
        else setStatus('ok');
      })
      .catch(() => { if (!parado) setStatus('erro'); });

    return () => { parado = true; ctrl.current?.stop(); };
  }, []);

  // Detecta quando o vídeo começa a reproduzir (câmera ativa)
  const handleVideoPlay = () => setStatus('ok');

  useEffect(() => {
    const fn = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [onClose]);

  return (
    <div className="scanner-overlay" onClick={onClose}>
      <div className="scanner-modal" onClick={e => e.stopPropagation()}>

        <div className="scanner-header">
          <span className="scanner-titulo">Leitura de Código de Barras</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="scanner-viewport">
          {status === 'erro' && (
            <div className="scanner-msg">
              <span style={{ fontSize: 36 }}>📷</span>
              <p>Não foi possível acessar a câmera.</p>
              <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>Verifique as permissões do navegador.</p>
            </div>
          )}
          {status === 'iniciando' && (
            <div className="scanner-msg">
              <div className="loading-spin" style={{ width: 28, height: 28, borderWidth: 3 }} />
              <p>Iniciando câmera...</p>
            </div>
          )}

          <video
            ref={videoRef}
            className="scanner-video"
            style={{ display: status === 'ok' ? 'block' : 'none' }}
            onPlay={handleVideoPlay}
            muted
            playsInline
          />

          {status === 'ok' && (
            <div className="scanner-frame">
              <span className="sc-c sc-c--tl" /><span className="sc-c sc-c--tr" />
              <span className="sc-c sc-c--bl" /><span className="sc-c sc-c--br" />
              <span className="scanner-line" />
            </div>
          )}
        </div>

        <div className="scanner-dica">
          {status === 'ok' ? 'Aponte o código de barras para a área acima' : '\u00A0'}
        </div>

      </div>
    </div>
  );
}

function CameraBtn({ onClick }) {
  return (
    <button className="cam-btn" onClick={onClick} title="Escanear código de barras">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 7V4a1 1 0 0 1 1-1h3M16 3h3a1 1 0 0 1 1 1v3M22 17v3a1 1 0 0 1-1 1h-3M8 21H5a1 1 0 0 1-1-1v-3"/>
        <rect x="7" y="7" width="10" height="10" rx="1"/>
      </svg>
    </button>
  );
}

function Campo({ label, valor }) {
  return (
    <div className="modal-campo">
      <span className="modal-campo-label">{label}</span>
      <span className="modal-campo-valor">{valor ?? '—'}</span>
    </div>
  );
}

function SubTabelaCompras({ dados }) {
  if (!dados.length) return <p style={{ padding: '12px 0', color: 'var(--text-dim)', fontSize: 13 }}>Nenhuma compra encontrada.</p>;
  return (
    <div style={{ overflowX: 'auto', marginTop: 8 }}>
      <table className="prod-hist-tbl">
        <thead>
          <tr>
            <th>Entrada</th>
            <th>Emissão</th>
            <th>NF</th>
            <th>Fornecedor</th>
            <th>Qtd NF</th>
            <th>UN</th>
            <th>Qtd Entrada</th>
            <th>UN Venda</th>
            <th className="prod-hist-right">Custo Unit.</th>
            <th className="prod-hist-right">Total NF</th>
          </tr>
        </thead>
        <tbody>
          {dados.map((d, i) => (
            <tr key={i}>
              <td>{fmtData(d.DATA_ENTRADA)}</td>
              <td>{fmtData(d.DATA_EMISSAO)}</td>
              <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.NFE_NUMERO || '—'}</td>
              <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.FORNECEDOR || '—'}</td>
              <td>{fmtNum(d.QUANTIDADE_NFE)}</td>
              <td style={{ color: 'var(--text-dim)' }}>{d.UN_COMPRA || '—'}</td>
              <td style={{ fontWeight: 700 }}>{fmtNum(d.QUANTIDADE_ENTRADA)}</td>
              <td style={{ color: 'var(--text-dim)' }}>{d.UN_VENDA || '—'}</td>
              <td className="prod-hist-right prod-hist-laranja">{fmt(d.UNITARIO_COMPRA)}</td>
              <td className="prod-hist-right">{fmt(d.TOTAL_COMPRA)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubTabelaVendas({ dados }) {
  if (!dados.length) return <p style={{ padding: '12px 0', color: 'var(--text-dim)', fontSize: 13 }}>Nenhuma venda encontrada nos últimos 90 dias.</p>;
  return (
    <div style={{ overflowX: 'auto', marginTop: 8 }}>
      <table className="prod-hist-tbl">
        <thead>
          <tr>
            <th>Mês</th>
            <th>Ano</th>
            <th className="prod-hist-right">Qtd</th>
            <th>UN</th>
            <th className="prod-hist-right">Total</th>
            <th className="prod-hist-right">Preço Médio</th>
          </tr>
        </thead>
        <tbody>
          {dados.map((d, i) => (
            <tr key={i}>
              <td><strong>{d.MES_NOME}</strong></td>
              <td style={{ color: 'var(--text-dim)' }}>{d.ANO}</td>
              <td className="prod-hist-right">{fmtNum(d.QUANTIDADE)}</td>
              <td style={{ color: 'var(--text-dim)' }}>{d.UND || '—'}</td>
              <td className="prod-hist-right prod-hist-laranja">{fmt(d.TOTAL)}</td>
              <td className="prod-hist-right">{fmt(d.PRECO_MEDIO)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProdutoModal({ produto, onClose }) {
  const p     = produto;
  const ativo = p.STATUS === 1;
  const promo = emPromocao(p);

  const [subView, setSubView] = useState(null); // null | 'compras' | 'vendas'
  const [subDados, setSubDados] = useState([]);
  const [subLoad,  setSubLoad]  = useState(false);
  const [subErro,  setSubErro]  = useState('');

  const abrirSub = async (tipo) => {
    if (subView === tipo) { setSubView(null); return; }
    setSubView(tipo);
    setSubDados([]);
    setSubLoad(true);
    setSubErro('');
    try {
      const dados = tipo === 'compras'
        ? await getProdutoUltimasCompras(p.ID)
        : await getProdutoUltimasVendas(p.ID);
      setSubDados(dados);
    } catch (e) {
      setSubErro(e.message);
    } finally {
      setSubLoad(false);
    }
  };

  return (
    <Modal titulo={p.PRODUTO} onClose={onClose}>

      <div>
        <div className="modal-secao-titulo">Identificação</div>
        <div className="modal-grid">
          <Campo label="ID" valor={p.ID} />
          <Campo label="EAN / Código" valor={p.EAN || '—'} />
          <Campo label="Unidade" valor={p.UNIDADE || '—'} />
          <Campo label="Status" valor={<Badge valor={ativo ? 'Ativo' : 'Inativo'} tipo={ativo ? 'success' : 'danger'} />} />
        </div>
      </div>

      <div>
        <div className="modal-secao-titulo">Preços</div>
        <div className="modal-grid">
          <Campo label="Custo (Compra)" valor={fmt(p.COMPRA)} />
          <Campo
            label="Preço de Venda"
            valor={
              promo
                ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ textDecoration: 'line-through', color: 'var(--text-dim)', fontSize: 12 }}>{fmt(p.VENDA)}</span>
                    <span style={{ fontWeight: 700, color: 'var(--green)' }}>{fmt(p.VALOR_DA_PROMOCAO)}</span>
                    <Badge valor="Promo" tipo="warning" />
                  </span>
                : <span style={{ fontWeight: 700 }}>{fmt(p.VENDA)}</span>
            }
          />
        </div>
      </div>

      {(p.VALOR_DA_PROMOCAO || p.PROMOCAO_DATA_INICIO || p.PROMOCAO_DATA_FINAL) && (
        <div className="modal-promo-box">
          <div className="modal-promo-titulo">{promo ? '🏷 Promoção ativa' : 'Promoção'}</div>
          <div className="modal-promo-grid">
            <Campo label="Valor Promo" valor={fmt(p.VALOR_DA_PROMOCAO)} />
            <Campo label="Início" valor={fmtData(p.PROMOCAO_DATA_INICIO)} />
            <Campo label="Fim" valor={fmtData(p.PROMOCAO_DATA_FINAL)} />
          </div>
        </div>
      )}

      <div>
        <div className="modal-grid">
          <Campo
            label="Estoque Físico"
            valor={<span style={{ fontWeight: 700, color: p.ESTOQUEFISICO > 0 ? 'var(--green)' : 'var(--red)' }}>{fmtNum(p.ESTOQUEFISICO ?? 0)}</span>}
          />
          <Campo label="Estoque Mínimo" valor={fmtNum(p.ESTOQUEMINIMO ?? 0)} />
        </div>
      </div>

      <div>
        <div className="modal-secao-titulo">Datas</div>
        <div className="modal-grid">
          <Campo label="Última Compra" valor={fmtData(p.DATA_ULTIMA_COMPRA)} />
          <Campo label="Última Venda"  valor={fmtData(p.DATA_ULTIMA_VENDA)} />
        </div>
      </div>

      <div>
        <div className="modal-secao-titulo">Histórico</div>
        <div className="prod-hist-btns">
          <button
            className={`prod-hist-btn${subView === 'compras' ? ' prod-hist-btn--ativo' : ''}`}
            onClick={() => abrirSub('compras')}
          >
            Últimas Compras
          </button>
          <button
            className={`prod-hist-btn${subView === 'vendas' ? ' prod-hist-btn--ativo' : ''}`}
            onClick={() => abrirSub('vendas')}
          >
            Últimas Vendas
          </button>
        </div>

        {subView && (
          <div className="prod-hist-painel">
            {subLoad && <Loading />}
            {subErro && <Erro mensagem={subErro} onRetry={() => abrirSub(subView)} />}
            {!subLoad && !subErro && subView === 'compras' && <SubTabelaCompras dados={subDados} />}
            {!subLoad && !subErro && subView === 'vendas'  && <SubTabelaVendas  dados={subDados} />}
          </div>
        )}
      </div>

    </Modal>
  );
}

export default function Produtos() {
  const [busca,    setBusca]    = useState('');
  const [status,   setStatus]   = useState('ativo');
  const [dados,    setDados]    = useState([]);
  const [load,     setLoad]     = useState(false);
  const [erro,     setErro]     = useState('');
  const [offset,   setOffset]   = useState(0);
  const [produto,  setProduto]  = useState(null);
  const [scanner,  setScanner]  = useState(false);

  const carregar = useCallback(async (b = busca, o = offset, s = status) => {
    setLoad(true); setErro('');
    try { setDados(await getProdutos(b, LIMITE, o, s)); }
    catch (e) { setErro(e.message); }
    finally { setLoad(false); }
  }, [busca, offset, status]);

  useEffect(() => { carregar(); }, [offset]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); carregar(busca, 0, status); }, 400);
    return () => clearTimeout(t);
  }, [busca]); // eslint-disable-line
  useEffect(() => { setOffset(0); carregar(busca, 0, status); }, [status]); // eslint-disable-line

  return (
    <div>
      <PageHeader titulo="Produtos" subtitulo={`${dados.length} registros exibidos`}>
        <select
          className="prod-status-select"
          value={status}
          onChange={e => setStatus(e.target.value)}
        >
          <option value="ativo">Ativo</option>
          <option value="inativo">Inativo</option>
          <option value="todos">Todos</option>
        </select>
        <SearchBar value={busca} onChange={setBusca} placeholder="Buscar descrição ou EAN..." />
        <CameraBtn onClick={() => setScanner(true)} />
      </PageHeader>

      {erro && <Erro mensagem={erro} onRetry={carregar} />}
      {load ? <Loading /> : <>
        <Table colunas={colunas} dados={dados} onRowClick={setProduto} vazio="Nenhum produto encontrado." />
        <Paginacao offset={offset} limite={LIMITE} total={dados.length} onChange={setOffset} />
      </>}

      {produto && <ProdutoModal produto={produto} onClose={() => setProduto(null)} />}
      {scanner && (
        <BarcodeScanner
          onDetect={codigo => { setBusca(codigo); setScanner(false); }}
          onClose={() => setScanner(false)}
        />
      )}
    </div>
  );
}
